import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Close } from '@mui/icons-material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { Button, IconButton } from '@mui/material';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import { useConnectorsContextProvider } from 'features/connectors/context/connectors-provider';
import { ConnectorType } from 'features/connectors/domain/connector';
import styles from 'features/connectors/views/connector-details/connector-details-popup-header.module.scss';

const ConnectorDetailsPopupHeader = () => {
  const { t } = useTranslation('connectors');
  const { hasPermission } = usePermissionAuthorization();

  const { formState } = useFormContext();
  const { connectorDetailsHook } = useConnectorsContextProvider();
  const { cancelMutation, mutationIsLoading, currentSelectedConnector, openDetailsInEditingMode, closeDetails, deleteConnector } =
    connectorDetailsHook;

  const isViewing = connectorDetailsHook.viewingMode === 'viewing';
  const isEditing = connectorDetailsHook.viewingMode === 'editing';
  const isCreating = connectorDetailsHook.viewingMode === 'creation';
  const isVcareConnector = currentSelectedConnector?.type === ConnectorType.Vcare;

  return (
    <div className={styles.container}>
      {isCreating && hasPermission(Permission.CreateConnectors) && (
        <>
          <Button
            data-testid="connectorDetailCancelButton"
            variant="text"
            onClick={() => closeDetails(formState.isDirty)}
            disabled={mutationIsLoading}
          >
            {t('details.cancelFormButton')}
          </Button>
          <Button
            data-testid="connectorDetailSubmitButton"
            startIcon={<CheckRounded />}
            variant="contained"
            type="submit"
            loading={mutationIsLoading}
            loadingPosition="start"
          >
            {t('details.saveFormButton')}
          </Button>
        </>
      )}

      {isEditing && (
        <>
          <Button data-testid="connectorDetailCancelButton" variant="text" onClick={() => cancelMutation()} disabled={mutationIsLoading}>
            {t('details.cancelFormButton')}
          </Button>
          <Button
            data-testid="connectorDetailSubmitButton"
            startIcon={<CheckRounded />}
            variant="contained"
            type="submit"
            loading={mutationIsLoading}
            loadingPosition="start"
          >
            {t('details.saveFormButton')}
          </Button>
        </>
      )}
      {isViewing && (
        <>
          {isVcareConnector && hasPermission(Permission.DeleteConnectors) && (
            <Button
              data-testid="connectorDetailRemoveButton"
              startIcon={<DeleteForeverOutlinedIcon />}
              variant="text"
              onClick={() => deleteConnector(currentSelectedConnector)}
              disabled={mutationIsLoading}
            >
              {t('details.removeFormButton')}
            </Button>
          )}
          {hasPermission(Permission.UpdateConnectors) && (
            <Button
              data-testid="connectorDetailEditButton"
              startIcon={<ModeEditOutlineOutlinedIcon />}
              variant="text"
              onClick={() => openDetailsInEditingMode(currentSelectedConnector!)}
              disabled={mutationIsLoading}
            >
              {t('details.editFormButton')}
            </Button>
          )}
        </>
      )}

      <IconButton
        aria-label="close"
        className={styles.closeButton}
        onClick={() => closeDetails(formState.isDirty)}
        data-testid="ConnectorDetailsPopupCloseButton"
      >
        <Close />
      </IconButton>
    </div>
  );
};

export default ConnectorDetailsPopupHeader;
